import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import Button, { ButtonStyle } from '@components/buttons/Button/Button';
import { NavBar } from '@components/navigation/NavBar/NavBar';
import Elliptical from '@components/spinners/Elliptical/Elliptical';
import {
    ChargebeePricingEtlService,
    IChargebeePricingEtlStatus,
} from '@services/chargebeePricingEtl/ChargebeePricingEtlService';
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';

/**
 * Manual trigger for the Chargebee pricing ETL (TP-6764).
 *
 * Unlisted page, reached by direct link. Two independent buttons - export Chargebee to CSV, then
 * load those CSVs into the pricing tables - each showing when it last happened and whether
 * something is running now. Deliberately not wrapped in AutoLogout: that only resets its timer on
 * click/keypress, and a scheduled export in production runs for the better part of an hour, so any
 * of it spent watching the page would trip the warning.
 */

const COUNTRIES = ['GB', 'AU'] as const;
type CountryCode = typeof COUNTRIES[number];

const EXPORT_ENTITIES = ['plans', 'addons', 'coupons'] as const;

/** Poll fast while something is happening, slowly while idle. */
const ACTIVE_POLL_MS = 5_000;
const IDLE_POLL_MS = 30_000;
/**
 * CloudWatch lambda metrics lag by a minute or two, so for this long after a button press we
 * report "running" from local state rather than believing a poll that says otherwise.
 */
const OPTIMISTIC_RUNNING_MS = 120_000;

const pageStyle: CSSProperties = {
    margin: '0 auto',
    maxWidth: '900px',
    padding: '24px',
};

const headingStyle: CSSProperties = {
    color: '#2C2A2D',
    fontSize: '20px',
    fontWeight: 600,
    marginBottom: '4px',
};

const introStyle: CSSProperties = {
    color: '#6B7280',
    fontSize: '13px',
    lineHeight: 1.5,
    marginBottom: '12px',
};

/**
 * The "in progress" state for the first two minutes after a press is held in local state, because
 * the CloudWatch metrics the status endpoint reads lag behind reality. A refresh discards it, so
 * the page briefly forgets a run is under way and re-enables the button.
 */
const refreshWarningStyle: CSSProperties = {
    backgroundColor: '#FEF3C7',
    border: '1px solid #FDE68A',
    borderRadius: '6px',
    color: '#92400E',
    fontSize: '13px',
    lineHeight: 1.5,
    marginBottom: '20px',
    padding: '10px 14px',
};

const countryToggleStyle: CSSProperties = {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
};

const panelStyle: CSSProperties = {
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    marginBottom: '16px',
    padding: '16px 20px',
};

const panelHeadingStyle: CSSProperties = {
    color: '#2C2A2D',
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '8px',
};

const detailStyle: CSSProperties = {
    color: '#4B5563',
    fontSize: '13px',
    lineHeight: 1.6,
};

const timestampStyle: CSSProperties = {
    fontFamily: 'monospace',
};

const runningPillStyle: CSSProperties = {
    backgroundColor: '#FEF3C7',
    border: '1px solid #FDE68A',
    borderRadius: '12px',
    color: '#92400E',
    fontSize: '12px',
    fontWeight: 600,
    marginLeft: '8px',
    padding: '2px 10px',
};

/**
 * Elliptical is 80x25, so it sits on a heading line; Circular is 80x80 and does not. The pill next
 * to it carries the words - the spinner is the at-a-glance signal that something is still moving.
 */
const spinnerStyle: CSSProperties = {
    display: 'inline-block',
    marginLeft: '4px',
    verticalAlign: 'middle',
};

const failurePillStyle: CSSProperties = {
    ...runningPillStyle,
    backgroundColor: '#FEE2E2',
    border: '1px solid #FECACA',
    color: '#B91C1C',
};

const blockedNoteStyle: CSSProperties = {
    color: '#92400E',
    fontSize: '12px',
    fontStyle: 'italic',
    marginTop: '8px',
};

const errorNoteStyle: CSSProperties = {
    color: '#B91C1C',
    fontSize: '13px',
    marginTop: '8px',
};

const messageNoteStyle: CSSProperties = {
    color: '#166534',
    fontSize: '13px',
    marginTop: '8px',
};

const caveatStyle: CSSProperties = {
    color: '#6B7280',
    fontSize: '12px',
    fontStyle: 'italic',
    marginTop: '8px',
};

/**
 * The shared Button sets the native `disabled` attribute but keeps its full-colour inline
 * background, which overrides the browser's default greying - so a disabled button looks exactly
 * like an available one. Dim the wrapper instead, as AdditionalDriverInfo and CustomerSearchBar do.
 * It has to be the wrapper: a className cannot beat the Button's inline style.
 */
const buttonRowStyle = (blocked: boolean): CSSProperties => ({
    marginTop: '12px',
    opacity: blocked ? 0.5 : 1,
    pointerEvents: blocked ? 'none' : 'auto',
});

/** Render an ISO timestamp in the viewer's locale, or a placeholder when it has never happened. */
export const formatTimestamp = (isoTimestamp: string | null): string => {
    if (!isoTimestamp) {
        return 'never';
    }
    const parsed = new Date(isoTimestamp);
    return isNaN(parsed.getTime()) ? isoTimestamp : parsed.toLocaleString();
};

const ChargebeePricingEtlTriggerView = () => {
    const msalContext = useMsal();
    const isAuthenticated = useIsAuthenticated();

    const [country, setCountry] = useState<CountryCode>('GB');
    const [status, setStatus] = useState<IChargebeePricingEtlStatus | null>(null);
    // Kept apart from actionError so that a successful poll cannot silently erase the reason a
    // button press was refused, and vice versa.
    const [statusError, setStatusError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    // Set on a button press and cleared once the optimistic window expires. A boolean plus a
    // timer rather than a "pressed at" comparison, so that the expiry itself causes a re-render.
    const [exportOptimistic, setExportOptimistic] = useState(false);
    const [loadOptimistic, setLoadOptimistic] = useState(false);

    const tokenParams = useRef({ msalContext, isAuthenticated });
    tokenParams.current = { msalContext, isAuthenticated };

    const refresh = useCallback(async (forCountry: CountryCode) => {
        try {
            const fetched = await ChargebeePricingEtlService.getStatus(forCountry, tokenParams.current);
            setStatus(fetched);
            setStatusError(null);
        } catch (fetchError: any) {
            setStatusError(fetchError?.message ?? 'Could not read the ETL status');
        }
    }, []);

    useEffect(() => {
        if (!exportOptimistic) return;
        const timer = setTimeout(() => setExportOptimistic(false), OPTIMISTIC_RUNNING_MS);
        return () => clearTimeout(timer);
    }, [exportOptimistic]);

    useEffect(() => {
        if (!loadOptimistic) return;
        const timer = setTimeout(() => setLoadOptimistic(false), OPTIMISTIC_RUNNING_MS);
        return () => clearTimeout(timer);
    }, [loadOptimistic]);

    const exportRunning = (status?.export.inFlight ?? false) || exportOptimistic;
    const loadRunning = (status?.load.running ?? false) || loadOptimistic;
    const anythingRunning = exportRunning || loadRunning;

    // Re-fetch on country change, then poll. The interval is a dependency so that switching
    // between active and idle cadence tears down the old timer.
    const pollInterval = anythingRunning ? ACTIVE_POLL_MS : IDLE_POLL_MS;
    useEffect(() => {
        refresh(country);
        const timer = setInterval(() => refresh(country), pollInterval);
        return () => clearInterval(timer);
    }, [country, pollInterval, refresh]);

    const onTrigger = async (
        trigger: (forCountry: string, params: any) => Promise<string>,
        markOptimistic: (running: boolean) => void,
    ) => {
        setMessage(null);
        setActionError(null);
        try {
            const resultMessage = await trigger(country, tokenParams.current);
            markOptimistic(true);
            setMessage(resultMessage);
        } catch (triggerError: any) {
            setActionError(triggerError?.message ?? 'The request was refused');
        }
        refresh(country);
    };

    // Each button is blocked by *either* job, mirroring the backend's own guards. The cross-block
    // has to consider the optimistic state and not just status.blocked: for the first minute or two
    // after a press, local state is the only thing that knows a job started, so relying on the
    // polled `blocked` fields alone would leave the other button live long enough to start a
    // conflicting run - and the backend would allow it, reading the same lagging metric.
    const exportBlockedReason = exportRunning
        ? 'An export is in progress'
        : loadRunning
            ? 'A pricing load is in progress'
            : status?.blocked.export ?? null;
    const loadBlockedReason = loadRunning
        ? 'A load is in progress'
        : exportRunning
            ? 'A Chargebee export is in progress'
            : status?.blocked.load ?? null;

    return (
        <React.Fragment>
            <NavBar pageTitle='Chargebee pricing ETL' />
            <div style={pageStyle}>
                <div style={headingStyle}>Chargebee pricing ETL</div>
                <div style={introStyle}>
                    Two steps, run in order. <strong>Export</strong> asks Chargebee for fresh CSV
                    files and saves them to S3. <strong>Load</strong> reads those files into the
                    pricing tables the quote builder uses. Both steps also run on a schedule on
                    their own. Only one of them should run at a time, so starting either one
                    disables both buttons until it is done.
                </div>
                <div style={introStyle}>
                    <strong>The export is slow - expect it to take about an hour.</strong> The load
                    is much shorter. Neither has a set run time: both depend on how much data is in
                    the Chargebee environment being read, and AU and GB are separate environments.
                    So the two countries will not take the same time, and the same country can vary
                    from one run to the next. Treat the timings above as a rough guide only.
                </div>
                <div style={introStyle}>
                    This page works out what is running from AWS monitoring data, and that data
                    arrives a couple of minutes behind. So a step can keep showing as in progress
                    for a few minutes after it has really finished. That is normal and does not
                    mean anything is stuck.
                </div>
                <div style={refreshWarningStyle} data-testid='refresh-warning'>
                    <strong>Please do not refresh this page after pressing a button.</strong> For
                    the first couple of minutes this page is the only thing that knows you started
                    something. Refreshing loses that, and a button can look available again while
                    the job is still running. Leave the page open instead - it updates itself.
                </div>

                <div style={countryToggleStyle}>
                    {COUNTRIES.map(option => (
                        <Button
                            key={option}
                            label={option}
                            testId={`country-${option}`}
                            btnType={option === country ? ButtonStyle.primary : ButtonStyle.secondary}
                            onClickFunc={() => setCountry(option)}
                        />
                    ))}
                </div>

                <div style={panelStyle}>
                    <div style={panelHeadingStyle}>
                        1. Export Chargebee to CSV
                        {exportRunning && (
                            <React.Fragment>
                                <span style={runningPillStyle}>in progress</span>
                                <span style={spinnerStyle} data-testid='export-spinner'>
                                    <Elliptical />
                                </span>
                            </React.Fragment>
                        )}
                    </div>
                    <div style={detailStyle}>
                        {EXPORT_ENTITIES.map(entity => (
                            <div key={entity}>
                                {entity}.csv last written{' '}
                                <span style={timestampStyle} data-testid={`export-${entity}`}>
                                    {formatTimestamp(status?.export.files?.[entity] ?? null)}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div style={buttonRowStyle(exportBlockedReason !== null)}
                        data-testid='run-export-row'>
                        <Button
                            label='Run export'
                            testId='run-export'
                            btnType={ButtonStyle.primary}
                            disabled={exportBlockedReason !== null}
                            onClickFunc={() => onTrigger(
                                ChargebeePricingEtlService.triggerExport, setExportOptimistic)}
                        />
                    </div>
                    {exportBlockedReason && (
                        <div style={blockedNoteStyle} data-testid='export-blocked'>
                            {exportBlockedReason}
                        </div>
                    )}
                </div>

                <div style={panelStyle}>
                    <div style={panelHeadingStyle}>
                        2. Load CSVs into pricing
                        {loadRunning && (
                            <React.Fragment>
                                <span style={runningPillStyle}>in progress</span>
                                <span style={spinnerStyle} data-testid='load-spinner'>
                                    <Elliptical />
                                </span>
                            </React.Fragment>
                        )}
                        {status?.load.recentFailure && (
                            <span style={failurePillStyle} data-testid='load-failure'>
                                recent failure
                            </span>
                        )}
                    </div>
                    <div style={detailStyle}>
                        Pricing last changed{' '}
                        <span style={timestampStyle} data-testid='pricing-last-changed'>
                            {formatTimestamp(status?.load.pricingLastChanged ?? null)}
                        </span>
                    </div>
                    <div style={caveatStyle}>
                        This is the last time a price actually changed, not the last time the load ran.
                        A load that only withdrew plans, or that found nothing new, will not move it.
                    </div>
                    <div style={buttonRowStyle(loadBlockedReason !== null)}
                        data-testid='run-load-row'>
                        <Button
                            label='Run load'
                            testId='run-load'
                            btnType={ButtonStyle.primary}
                            disabled={loadBlockedReason !== null}
                            onClickFunc={() => onTrigger(
                                ChargebeePricingEtlService.triggerLoad, setLoadOptimistic)}
                        />
                    </div>
                    {loadBlockedReason && (
                        <div style={blockedNoteStyle} data-testid='load-blocked'>
                            {loadBlockedReason}
                        </div>
                    )}
                </div>

                {message && <div style={messageNoteStyle} data-testid='trigger-message'>{message}</div>}
                {actionError && <div style={errorNoteStyle} data-testid='trigger-error'>{actionError}</div>}
                {statusError && <div style={errorNoteStyle} data-testid='status-error'>{statusError}</div>}
            </div>
        </React.Fragment>
    );
};

export default ChargebeePricingEtlTriggerView;
